import { defaultTheme, RaThemeOptions } from 'react-admin';
import merge from 'lodash/merge';

/**
 * Values mirror CSS custom properties declared in src/app.css.
 * Keep in sync — MUI v5 cannot read CSS vars from the palette directly
 * (it manipulates colors internally for hover/active states).
 */
const darkColors = {
  primary: '#FD8A32',
  secondary: '#272727',
  background: '#000000',
  foreground: '#272727',
  error: '#FF0000',
  title: '#ffffff',
  caption: '#ffffff',
};

const lightColors = {
  primary: '#042E6B',
  secondary: '#272727',
  background: '#E0E5ED',
  foreground: '#ffffff',
  error: '#FF0000',
  title: '#000000',
  caption: '#000000',
};

type Palette = typeof darkColors;

const buildTheme = (
  mode: 'light' | 'dark',
  c: Palette,
  divider: string,
): RaThemeOptions => merge({}, defaultTheme, {
  palette: {
    mode,
    primary: {
      main: c.primary,
      contrastText: mode === 'dark' ? c.title : '#ffffff',
    },
    secondary: {
      main: c.secondary,
      contrastText: '#ffffff',
    },
    error: {
      main: c.error,
      contrastText: '#ffffff',
    },
    background: {
      default: c.background,
      paper: c.foreground,
    },
    text: {
      primary: c.title,
      secondary: c.caption,
    },
    divider,
  },
  typography: {
    fontFamily: 'var(--font-primary)',
    allVariants: {
      color: c.title,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': {
          padding: 0,
          scrollbarWidth: 'thin',
          backgroundColor: c.background,
          color: c.title,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: c.foreground,
          backgroundImage: 'none',
          color: c.title,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: c.foreground,
          color: c.title,
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: c.foreground,
          color: c.title,
        },
      },
    },
    // Default/info notifications (react-admin `notify`) render through MUI
    // SnackbarContent, whose background MUI derives from background.default —
    // which turns near-white in dark mode. Style it like an app surface so it
    // matches the selected theme. Semantic success/error/warning keep their
    // own colors via the more specific RaNotification-* classes.
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: c.foreground,
          color: c.title,
          border: `1px solid ${c.primary}`,
          borderRadius: 8,
        },
      },
    },
  },
  sidebar: {
    width: 250,
    closedWidth: 70,
  },
});

export const darkTheme = buildTheme('dark', darkColors, 'rgba(255, 255, 255, 0.12)');
export const lightTheme = buildTheme('light', lightColors, 'rgba(0, 0, 0, 0.12)');

/** @deprecated use darkTheme / lightTheme directly. Kept for back-compat. */
export const theme = darkTheme;
